// Self-service sign-up page.
//
// Flow: (1) register with email + password; Cognito emails a verification code. (2) Enter
// the code to confirm the account. After confirmation the user signs in, and because MFA
// is mandatory they are then driven through authenticator enrollment before the dashboard
// is reachable. So account creation requires email verification, and access additionally
// requires MFA.

import { useState, type FormEvent } from "react";
import { Bell, Check, Share2, ShieldCheck, TrendingUp } from "lucide-react";

import { confirmSignUp, resendConfirmationCode, signUp } from "../auth/cognito";
import { INVITE_PITCH, useInviteShare } from "../lib/useInviteShare";
import { BrandMark } from "../components/BrandMark";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// A one-line pitch plus the three things the app does, shown above the sign-up card so a
// brand-new visitor understands what they are joining before creating an account.
const PITCH = INVITE_PITCH;

const HIGHLIGHTS: { icon: typeof ShieldCheck; title: string; body: string }[] = [
  {
    icon: ShieldCheck,
    title: "Know your loan-to-value",
    body: "Link a brokerage and smart-margin tracks your margin and account value for you.",
  },
  {
    icon: Bell,
    title: "Alerts before trouble",
    body: "Set your own thresholds and get an email or Telegram ping before a margin call.",
  },
  {
    icon: TrendingUp,
    title: "Watch what matters",
    body: "Track prices, drawdown, and risk for the funds and stocks you care about.",
  },
];

/** A short, friendly intro shown above the sign-up card. */
function Intro({ onShare, shareLabel }: { onShare: () => void; shareLabel: string }) {
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <BrandMark className="size-10 shrink-0" />
        <div>
          <p className="text-xl font-semibold tracking-tight text-foreground">
            smart<span className="text-primary">-margin</span>
          </p>
          <p className="text-sm text-muted-foreground">{PITCH}</p>
        </div>
      </div>

      <ul className="space-y-3">
        {HIGHLIGHTS.map(({ icon: Icon, title, body }) => (
          <li key={title} className="flex gap-3">
            <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Icon className="size-4" />
            </span>
            <div className="space-y-0.5">
              <p className="text-sm font-medium text-foreground">{title}</p>
              <p className="text-sm text-muted-foreground">{body}</p>
            </div>
          </li>
        ))}
      </ul>

      <p className="text-xs text-muted-foreground">
        Free to try. Set up takes about a minute, email verification and an authenticator
        app for two-factor sign-in.
      </p>

      <button
        type="button"
        onClick={onShare}
        className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
      >
        <Share2 className="size-4" />
        {shareLabel}
      </button>
    </div>
  );
}

export function Signup({ onSignedUp }: { onSignedUp: (email: string) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState<"register" | "confirm">("register");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const { share: shareInvite, label: shareLabel } = useInviteShare();

  const register = async (e: FormEvent) => {
    e.preventDefault();
    setStatus("");
    if (password !== confirmPassword) {
      setStatus("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await signUp(email.trim(), password);
      setStage("confirm");
      setStatus("We emailed you a verification code. Enter it below.");
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Sign-up failed");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    setStatus("");
    if (!/^\d{6}$/.test(code.trim())) {
      setStatus("Enter the 6-digit code from your email.");
      return;
    }
    setBusy(true);
    try {
      await confirmSignUp(email.trim(), code.trim());
      // Account verified. Hand the email back so the sign-in form can prefill it; the user
      // signs in, then enrolls MFA (mandatory) before reaching the dashboard.
      onSignedUp(email.trim());
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Confirmation failed");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setStatus("");
    try {
      await resendConfirmationCode(email.trim());
      setStatus("A new code is on its way.");
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Could not resend the code");
    }
  };

  if (stage === "confirm") {
    return (
      <section className="mx-auto flex min-h-[60vh] max-w-sm items-center">
        <Card className="w-full">
          <CardHeader className="items-center text-center">
            <span className="mb-2 flex size-12 items-center justify-center rounded-xl bg-primary/10">
              <Check className="size-6 text-primary" />
            </span>
            <CardTitle>Verify your email</CardTitle>
            <CardDescription>
              Enter the 6-digit code we emailed to {email}.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={(e) => void confirm(e)}>
              <div className="space-y-2">
                <Label htmlFor="signup-code">Verification code</Label>
                <Input
                  id="signup-code"
                  inputMode="numeric"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  aria-label="signup-code"
                  placeholder="123456"
                  autoComplete="one-time-code"
                />
              </div>
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? "Verifying…" : "Verify and continue"}
              </Button>
            </form>
            <Button
              type="button"
              variant="ghost"
              className="mt-2 w-full"
              onClick={() => void resend()}
              disabled={busy}
            >
              Resend code
            </Button>
            {status && <p className="status mt-3 text-sm text-muted-foreground">{status}</p>}
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <section className="mx-auto grid min-h-[60vh] max-w-4xl items-center gap-8 py-6 md:grid-cols-2">
      {/* Left: the friendly pitch, so a new visitor knows what they are signing up for. */}
      <Intro onShare={() => void shareInvite()} shareLabel={shareLabel} />

      {/* Right: the account form. */}
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Create your account</CardTitle>
          <CardDescription>
            Create an account, verify email, and set up two-factor authentication.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={(e) => void register(e)}>
            <div className="space-y-2">
              <Label htmlFor="signup-email">Email</Label>
              <Input
                id="signup-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-label="signup-email"
                autoComplete="username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-password">Password</Label>
              <Input
                id="signup-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-label="signup-password"
                autoComplete="new-password"
              />
              <p className="text-xs text-muted-foreground">
                At least 12 characters, with upper and lower case, a number, and a symbol.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-confirm">Confirm password</Label>
              <Input
                id="signup-confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                aria-label="signup-confirm"
                autoComplete="new-password"
              />
            </div>
            <Button type="submit" className="w-full" disabled={busy}>
              {busy ? "Creating…" : "Create account"}
            </Button>
          </form>
          {status && <p className="status mt-3 text-sm text-destructive">{status}</p>}
        </CardContent>
      </Card>
    </section>
  );
}
